import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { openCookiePreferences } from '../../lib/analyticsConsent';
import { buildFooterScheduleLines } from '../../lib/scheduleDisplay';
import { legalValue, mailtoHref, telHref, useLegalIdentity, waHref } from '../../lib/legalIdentity';

export const LEGAL_LINKS = [
  { to: '/aviso-de-privacidad', label: 'Aviso de privacidad' },
  { to: '/terminos', label: 'Términos y condiciones' },
  { to: '/cookies', label: 'Cookies' },
  { to: '/baja', label: 'Baja de promociones' },
];

/**
 * Pie legal (doc. 05 §1): identidad del proveedor, contacto, horario y enlaces RELATIVOS a las páginas legales.
 * Una línea con un dato vacío se omite (nunca se muestra un valor heredado).
 * `showCookiePrefs`: el botón «Preferencias de cookies» solo donde está montado el aviso de cookies.
 */
export default function LegalFooterBlock({ schedule = null, showCookiePrefs = true, className = '' }) {
  const { identity } = useLegalIdentity();
  const v = (k) => legalValue(identity, k);
  const scheduleText = useMemo(() => (schedule ? buildFooterScheduleLines(schedule).join(' · ') : ''), [schedule]);
  const placeLine = [v('address'), v('city')].filter(Boolean).join(' · ');
  const phone = v('phone');
  const wa = v('whatsapp');
  const mail = v('privacy_email');
  const year = new Date().getFullYear();
  const linkCls = 'underline underline-offset-2 hover:no-underline';

  return (
    <div className={`text-sm leading-relaxed space-y-1 ${className}`} data-testid="legal-footer">
      {v('store_brand') || v('trade_name') ? (
        <p>
          {v('store_brand') ? <strong>{v('store_brand')}</strong> : null}
          {v('trade_name') ? (
            <>
              {' '}— sitio de demostración de <strong>{v('trade_name')}</strong>
            </>
          ) : null}
        </p>
      ) : null}
      {v('responsible_name') ? (
        <p>
          Proveedor: {v('responsible_name')}
          {v('rfc') ? ` · RFC ${v('rfc')}` : ''}
        </p>
      ) : null}
      {placeLine ? <p>{placeLine}</p> : null}
      {phone || wa || mail ? (
        <p>
          {[
            phone ? (
              <span key="t">
                Tel. <a className={linkCls} href={telHref(phone)}>{phone}</a>
              </span>
            ) : null,
            wa ? (
              <span key="w">
                WhatsApp{' '}
                <a className={linkCls} href={waHref(wa)} target="_blank" rel="noreferrer">
                  {wa}
                </a>
              </span>
            ) : null,
            mail ? (
              <a key="m" className={`${linkCls} break-all`} href={mailtoHref(mail)}>
                {mail}
              </a>
            ) : null,
          ]
            .filter(Boolean)
            .reduce((acc, el, i) => (i ? [...acc, ' · ', el] : [el]), [])}
        </p>
      ) : null}
      {scheduleText ? <p>Horario: {scheduleText}</p> : null}
      <nav aria-label="Información legal" className="pt-1">
        <ul className="flex flex-wrap gap-x-3 gap-y-1">
          {LEGAL_LINKS.map((l) => (
            <li key={l.to}>
              <Link to={l.to} className={linkCls}>
                {l.label}
              </Link>
            </li>
          ))}
          {showCookiePrefs ? (
            <li>
              <button type="button" onClick={openCookiePreferences} className={linkCls} data-testid="cookie-preferences-link">
                Preferencias de cookies
              </button>
            </li>
          ) : null}
        </ul>
      </nav>
      <p>
        © {year} {v('trade_name') || v('store_brand')}. Precios en MXN, IVA incluido.
      </p>
    </div>
  );
}
